/**
 * The C0 tax-spine route table.
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `TAX_SPINE_ROUTES` in — the same shape
 * `CONSOL_ROUTES`, `HR_ROUTES` and the rest already use. The v1 tax routes
 * (`/tax/codes`, `/tax/report`) stay where they are in the router and are
 * untouched by this table.
 *
 * Two shapes are load-bearing:
 *
 *  - **Every route is gated on a *read* permission.** Activating a pack,
 *    accepting an update, editing a registration, approving a return — all are
 *    checked on the action, never on the route. An Auditor holds the read codes
 *    and nothing else, and must be able to open every one of these screens and
 *    change none of them. Gating a screen on a write permission would make the
 *    audit impossible while looking like security.
 *  - **`:country` is constrained to two uppercase letters** and `:id` to digits,
 *    so `/tax/packs/new` can never be parsed as a country and `/tax/returns/new`
 *    can never be parsed as a return id. There is deliberately no such route in
 *    either family: both are created from a dialog on the list, which keeps the
 *    list — the thing you are comparing against — on screen (§11's "never lose
 *    page context").
 */

import type { RouteRecordRaw } from 'vue-router'

import { TAX_SPINE_PERMISSIONS } from '@/modules/tax/permissions'

export const TAX_SPINE_ROUTES: RouteRecordRaw[] = [
  {
    path: '/tax/packs',
    name: 'tax-packs',
    component: () => import('@/modules/tax/PackActivationView.vue'),
    meta: { titleKey: 'tax.spine.routeTitles.packs', permission: TAX_SPINE_PERMISSIONS.packRead },
  },
  {
    path: '/tax/packs/:country([A-Z]{2})',
    name: 'tax-pack-country',
    component: () => import('@/modules/tax/PackUpdateView.vue'),
    meta: { titleKey: 'tax.spine.routeTitles.packUpdate', permission: TAX_SPINE_PERMISSIONS.packRead },
  },
  {
    path: '/tax/jurisdictions',
    name: 'tax-jurisdictions',
    component: () => import('@/modules/tax/JurisdictionsView.vue'),
    meta: {
      titleKey: 'tax.spine.routeTitles.jurisdictions',
      permission: TAX_SPINE_PERMISSIONS.jurisdictionRead,
    },
  },
  {
    path: '/tax/registrations',
    name: 'tax-registrations',
    component: () => import('@/modules/tax/RegistrationsView.vue'),
    meta: {
      titleKey: 'tax.spine.routeTitles.registrations',
      permission: TAX_SPINE_PERMISSIONS.registrationRead,
    },
  },
  {
    path: '/tax/exemption-certificates',
    name: 'tax-certificates',
    component: () => import('@/modules/tax/CertificatesView.vue'),
    meta: {
      titleKey: 'tax.spine.routeTitles.certificates',
      permission: TAX_SPINE_PERMISSIONS.certificateRead,
    },
  },
  {
    path: '/tax/returns',
    name: 'tax-returns',
    component: () => import('@/modules/tax/ReturnsView.vue'),
    meta: { titleKey: 'tax.spine.routeTitles.returns', permission: TAX_SPINE_PERMISSIONS.returnRead },
  },
  {
    path: '/tax/returns/:id(\\d+)',
    name: 'tax-return',
    component: () => import('@/modules/tax/ReturnDetailView.vue'),
    meta: { titleKey: 'tax.spine.routeTitles.return', permission: TAX_SPINE_PERMISSIONS.returnRead },
  },
  {
    path: '/tax/simulator',
    name: 'tax-simulator',
    component: () => import('@/modules/tax/TaxSimulator.vue'),
    meta: {
      titleKey: 'tax.spine.routeTitles.simulator',
      permission: TAX_SPINE_PERMISSIONS.simulate,
    },
  },
  {
    path: '/tax/charges',
    name: 'tax-charges',
    component: () => import('@/modules/tax/ChargePoolsView.vue'),
    meta: { titleKey: 'tax.spine.routeTitles.charges', permission: TAX_SPINE_PERMISSIONS.chargeRead },
  },
]
